import { useCallback, useState } from "react";
import api from "../lib/api";
import { getUserLocation } from "../lib/geo";
import { logSwallow } from "../lib/log";

/**
 * useLocationAccess — encapsulates the "does the browser have GPS + backend
 * has a fresh presence record" contract used by LiveRoom.
 *
 * Returns:
 *   - denied         (bool)  true when GPS access failed or was refused
 *   - reason         (string) "denied" | "unavailable" | "timeout" | "unsupported" | "iframe_blocked"
 *   - ensurePresence (fn)     always re-reads GPS and pings server; sets `denied` + `reason` on failure
 *   - clearDenied    (fn)     manually clear the denied state (used by "Retry")
 *
 * IMPORTANT — freshness policy (Jul 2026, real-world bug fix):
 *   The previous version short-circuited when ANY presence existed. That
 *   trapped users at their LAST pinged coordinates for up to 90 min
 *   (PRESENCE_TTL_MIN), which is why users physically together were being
 *   placed into DIFFERENT Live Room scopes (one still had home coords).
 *   Now we ALWAYS re-read GPS on ensurePresence(). If the phone returns a
 *   cached fix (< 60s old, per geo.js maximumAge), that's fine — but the
 *   coords are current-location, not last-visited-location.
 *   If GPS is unavailable / denied / times out AND a valid presence already
 *   exists, we don't hard-fail — we log the reason but treat the call as
 *   "OK, using existing presence" so the user isn't blocked by a transient
 *   GPS hiccup. This preserves the "just works" feel while eliminating the
 *   stale-coords-across-multiple-visits bug.
 */
const PING_STALENESS_MS = 30 * 1000; // don't re-ping more than once per 30s per tab

export function useLocationAccess() {
  const [denied, setDenied] = useState(false);
  const [reason, setReason] = useState("denied");

  const ensurePresence = useCallback(async () => {
    // 1) Try to always get FRESH coords (getUserLocation uses maximumAge=60s
    //    which is browser-cached, so this is cheap on repeat calls but
    //    guarantees the position matches the user's ACTUAL location, not a
    //    stale record from hours ago at a different place).
    let coords = null;
    let gpsErr = null;
    try {
      const r = await getUserLocation();
      coords = r.coords;
    } catch (e) {
      gpsErr = e;
    }

    // 2) If we have fresh coords, ping the server. Rate-limit to 1 ping / 30s
    //    per tab to avoid write storms during rapid navigation.
    if (coords) {
      const last = Number(sessionStorage.getItem("bl_last_ping") || 0);
      if (Date.now() - last > PING_STALENESS_MS) {
        try {
          await api.post("/presence/ping", {
            lat: coords.latitude,
            lng: coords.longitude,
            mode: "anonymous",
            vibe_tag: "live room",
            radius_m: 150,
          });
          sessionStorage.setItem("bl_last_ping", String(Date.now()));
        } catch (e) { logSwallow("location.presence.ping", e); }
      }
      return true;
    }

    // 3) GPS failed. If a valid presence record already exists on the server,
    //    keep the user in — otherwise this would falsely lock them out on a
    //    single flaky location read. Show the recovery UI only when there is
    //    truly no presence to work with.
    let hasServerPresence = false;
    try {
      const me = await api.get("/presence/me");
      hasServerPresence = !!me.data.presence;
    } catch (e) { logSwallow("location.presence.me", e); }

    if (hasServerPresence) {
      return true; // reuse existing presence; user can still see Live Room
    }

    setReason(gpsErr?.reason || "unavailable");
    setDenied(true);
    return false;
  }, []);

  const clearDenied = useCallback(() => setDenied(false), []);

  return { denied, reason, ensurePresence, clearDenied };
}
